/**
 * Generation Section
 * Sub-component of PreferencesModal for resume section accordion with generation type cycling.
 */
import { Brain, Settings, XCircle, SlidersHorizontal } from 'lucide-react';
import type { UserPreferences, SubcomponentType, GenerationType } from '../../types';
import { SUBCOMPONENTS, GENERATION_TYPE_RESTRICTIONS } from '../../types';
import { SUBCOMPONENT_LABELS } from '../../constants';

export interface GenerationSectionProps {
  preferences: UserPreferences;
  sectionNamesMap: Record<string, string>;
  onPreferencesChange: (updated: UserPreferences) => void;
  onSectionNamesMapChange: (updated: Record<string, string>) => void;
}

const typeIcons: Record<GenerationType, { icon: React.ReactNode; label: string; className: string }> = {
  ai: { icon: <Brain className="w-4 h-4 mr-1" />, label: 'AI (Select)', className: 'bg-purple-100 text-purple-700' },
  ai_rewrite: { icon: <SlidersHorizontal className="w-4 h-4 mr-1" />, label: 'AI (Generate)', className: 'bg-purple-200 text-purple-800' },
  manual: { icon: <Settings className="w-4 h-4 mr-1" />, label: 'Manual', className: 'bg-gray-200 text-gray-700' },
  omit: { icon: <XCircle className="w-4 h-4 mr-1" />, label: 'Omit', className: 'bg-red-100 text-red-700' },
};

export function GenerationSection({ preferences, sectionNamesMap, onPreferencesChange, onSectionNamesMapChange }: GenerationSectionProps) {
  function cycleGenerationType(component: SubcomponentType) {
    const key = `default_gen_${component}` as keyof UserPreferences;
    const current = preferences[key] as GenerationType;
    const restrictions = GENERATION_TYPE_RESTRICTIONS[component];
    const allowedTypes: GenerationType[] = restrictions || ['manual', 'ai', 'ai_rewrite', 'omit'];

    const currentIndex = allowedTypes.indexOf(current);
    const nextIndex = (currentIndex + 1) % allowedTypes.length;
    const newValue = allowedTypes[nextIndex];

    onPreferencesChange({ ...preferences, [key]: newValue });
  }

  function updateSectionName(component: SubcomponentType, value: string) {
    const updated = { ...sectionNamesMap };
    if (value.trim() === '') {
      delete updated[component];
    } else {
      updated[component] = value;
    }
    onSectionNamesMapChange(updated);
    const jsonStr = Object.keys(updated).length > 0 ? JSON.stringify(updated) : undefined;
    onPreferencesChange({ ...preferences, section_names: jsonStr as string | undefined });
  }

  return (
    <div className="p-3 space-y-2">
      <p className="text-xs text-gray-500 mb-3">
        Configure display names and default generation types for each resume section.
      </p>
      {SUBCOMPONENTS.map((component) => {
        const key = `default_gen_${component}` as keyof UserPreferences;
        const currentType = (preferences[key] || 'ai') as GenerationType;
        const typeConfig = typeIcons[currentType];

        return (
          <div
            key={component}
            className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
          >
            <input
              type="text"
              value={sectionNamesMap[component] || ''}
              onChange={(e) => updateSectionName(component, e.target.value)}
              placeholder={SUBCOMPONENT_LABELS[component]}
              className="flex-1 min-w-0 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
            />
            <button
              onClick={() => cycleGenerationType(component)}
              className={`flex items-center px-3 py-1 rounded-full text-sm whitespace-nowrap flex-shrink-0 ${typeConfig.className}`}
            >
              {typeConfig.icon}
              {typeConfig.label}
            </button>
          </div>
        );
      })}
    </div>
  );
}
